import { useEffect, useState } from 'react';

import { progress } from '@/api/endpoints';
import { Icon } from '@/components/ui/Icon';
import { Empty, Loading, Panel, ScreenBody, ScreenHeader } from '@/components/ui/primitives';

type Board = 'depth' | 'collection' | 'treasures' | 'coins';
const BOARDS: [Board, string][] = [
  ['depth', 'Depth'], ['treasures', 'Treasures'], ['coins', 'Coins'],
];
const UNIT: Record<Board, (n: number) => string> = {
  depth: (n) => `${n} m`,
  treasures: (n) => n.toLocaleString('en-US'),
  coins: (n) => n.toLocaleString('en-US'),
  collection: (n) => n.toLocaleString('en-US'),
};

export function Leaderboard({ onBack }: { onBack: () => void }) {
  const [board, setBoard] = useState<Board>('depth');
  const [data, setData] = useState<Awaited<ReturnType<typeof progress.leaderboard>> | null>(null);

  useEffect(() => {
    setData(null);
    void progress.leaderboard(board).then(setData);
  }, [board]);

  return (
    <ScreenBody>
      <ScreenHeader icon={<Icon name="leaderboard" size={24} />} title="Leaderboard" onBack={onBack} />

      <div className="mb-3 flex gap-1.5">
        {BOARDS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => setBoard(id)}
            className={`flex-1 rounded-lg border py-2 font-pixel text-[8px] uppercase ${
              board === id
                ? 'border-coin bg-[linear-gradient(#F5D06A,#D0A420)] text-[#101010]'
                : 'border-[var(--hairline)] bg-black/30 text-ink-dim'}`}>
            {label}
          </button>
        ))}
      </div>

      {!data ? <Loading /> : (
        <>
          {data.me && (
            <Panel title="YOUR RANK" className="mb-3">
              <p className="text-center">
                <span className="font-pixel text-lg text-torch-core tabular">
                  {data.me.rank ? `#${data.me.rank}` : 'unranked'}
                </span>
                <span className="ml-2 text-sm text-ink-dim tabular">
                  {UNIT[board](data.me.score)}
                </span>
              </p>
            </Panel>
          )}

          <Panel title={`TOP · ${data.period}`}>
            {data.results.length === 0 ? (
              <Empty>Nobody has posted a score for this period yet.</Empty>
            ) : (
              <ol className="space-y-0" role="list">
                {data.results.map((r) => (
                  <li key={`${r.rank}-${r.display_name}`}
                      className="flex items-center gap-3 border-t border-[var(--hairline)] py-2.5 first:border-t-0">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg
                                     bg-black/35 font-pixel text-[8px] tabular">
                      {/* `aria-hidden` used to sit on this whole span, so a screen reader
                          skipped the rank entirely — the trophy icon carries no text of
                          its own, and for 4th place and below it hid the plain number too.
                          One sr-only announcement here, same convention Icon.tsx already
                          uses elsewhere in this codebase. */}
                      <span className="sr-only">Rank {r.rank}</span>
                      {r.rank <= 3
                        ? <Icon name="trophy" size={18}
                                style={{ color: ['#F5C542', '#C7CBD1', '#C97A3D'][r.rank - 1] }} />
                        : <span aria-hidden>{r.rank}</span>}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-bold">
                      {r.display_name}
                    </span>
                    <span className="shrink-0 font-bold text-coin tabular">
                      {UNIT[board](r.score)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </>
      )}
    </ScreenBody>
  );
}
